<template>
  <div class="page-container">
    <PageHeader title="水电费记录" show-breadcrumb>
      <template #actions>
        <el-button type="warning" icon="el-icon-download" size="small" :loading="exporting" @click="handleExport">导出 Excel</el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-input v-model.trim="query.communityName" placeholder="小区名称" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-input v-model.trim="query.handlerName" placeholder="办理人姓名" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-input v-model.trim="query.handlerPhone" placeholder="办理人联系方式" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-input v-model.trim="query.householdNo" placeholder="户号" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-select v-model="query.aubType" placeholder="充值类型" clearable size="small" style="width: 130px">
        <el-option label="水费" :value="1" />
        <el-option label="电费" :value="2" />
      </el-select>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator=" 至 "
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        size="small"
        style="width: 300px"
        @change="handleDateChange"
      />

      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="提交记录">
      <el-table :data="list" v-loading="loading" size="small" stripe border>
        <el-table-column type="index" label="序号" width="80" align="center" />
        <el-table-column prop="communityName" label="小区名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="handlerName" label="办理人姓名" width="120" />
        <el-table-column prop="handlerPhone" label="办理人联系方式" width="150" />
        <el-table-column prop="householdNo" label="户号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="aubType" label="充值类型" width="100" align="center">
          <template slot-scope="{ row }">{{ row.aubType === 1 ? '水费' : row.aubType === 2 ? '电费' : '--' }}</template>
        </el-table-column>
        <el-table-column prop="handleAmount" label="充值金额" width="120" align="right">
          <template slot-scope="{ row }">{{ formatAmount(row.handleAmount) }}</template>
        </el-table-column>
        <el-table-column prop="checkImages" label="和包核销图片" width="130" align="center">
          <template slot-scope="{ row }">
            <el-button v-if="hasImages(row.checkImages)" type="text" size="mini" @click="showImages(row.checkImages)">查看</el-button>
            <span v-else class="text-muted">无</span>
          </template>
        </el-table-column>
        <el-table-column prop="createtime" label="提交时间" width="180" />
        <el-table-column label="操作" width="90" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" class="text-danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <el-dialog title="和包核销图片" :visible.sync="imageDialogVisible" width="700px">
      <div class="image-preview-list">
        <img v-for="(url, idx) in imageList" :key="idx" :src="url" class="image-preview-item" @click="previewImage(url)" />
        <div v-if="!imageList.length" class="text-muted" style="padding:20px;text-align:center;">暂无图片</div>
      </div>
    </el-dialog>

    <el-dialog :visible.sync="bigImageVisible" width="800px" :show-close="true" append-to-body>
      <img :src="bigImageUrl" style="width:100%" />
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { deleteAjefUtilityBill, exportAjefUtilityBill, getAjefUtilityBillList } from '@/api/api'

export default {
  name: 'UtilityBillList',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      exporting: false,
      list: [],
      query: { communityName: '', handlerName: '', handlerPhone: '', householdNo: '', aubType: '', dateStart: '', dateEnd: '' },
      dateRange: null,
      page: { currentPage: 1, pageSize: 10, total: 0 },
      imageDialogVisible: false,
      imageList: [],
      bigImageVisible: false,
      bigImageUrl: ''
    }
  },
  created () { this.fetchList() },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const res = await getAjefUtilityBillList({ ...this.query, currentPage: this.page.currentPage, pageSize: this.page.pageSize })
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleDateChange (val) {
      this.query.dateStart = val ? val[0] : ''
      this.query.dateEnd = val ? val[1] : ''
    },
    handleReset () {
      this.query = { communityName: '', handlerName: '', handlerPhone: '', householdNo: '', dateStart: '', dateEnd: '' }
      this.dateRange = null
      this.handleSearch()
    },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    formatAmount (value) {
      if (value === null || value === undefined || value === '') return '--'
      return Number(value).toFixed(2)
    },
    parseImages (images) {
      if (!images) return []
      if (Array.isArray(images)) return images.filter(Boolean)
      try {
        const parsed = JSON.parse(images)
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [images]
      } catch (e) {
        return String(images).split(',').map(item => item.trim()).filter(Boolean)
      }
    },
    hasImages (images) {
      return this.parseImages(images).length > 0
    },
    showImages (images) {
      this.imageList = this.parseImages(images)
      this.imageDialogVisible = true
    },
    previewImage (url) {
      this.bigImageUrl = url
      this.bigImageVisible = true
    },
    async handleDelete (row) {
      try {
        await this.$confirm('确认删除该水电费记录吗？', '提示', { type: 'warning' })
        await deleteAjefUtilityBill({ aubId: row.aubId })
        this.$message.success('删除成功')
        this.fetchList()
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error('删除失败')
      }
    },
    async handleExport () {
      this.exporting = true
      try {
        await exportAjefUtilityBill(this.query)
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.image-preview-list { display: flex; flex-wrap: wrap; gap: 12px; }
.image-preview-item { width: 120px; height: 120px; object-fit: cover; border-radius: 6px; cursor: pointer; border: 1px solid #e2e8f0; transition: transform 0.2s;
  &:hover { transform: scale(1.05); }
}
</style>
